<template>
  <div class="pos-container" v-loading="loading">
    <!-- 左侧分类导航 -->
    <MealCategorySidebar
      :active-category="currentCategory"
      :product-categorys="productCategorys"
      @category-change="handleCategoryChange"
    />

    <!-- 中间商品展示区 -->
    <MealGoodsGrid
      :products="products"
      @search="handleSearch"
      @product-click="handleProductClick"
    />

    <!-- 右侧购物车 -->
    <MealCartPanel
      :cart-items="cart"
      :is-vip="isVip"
      :member="member"
      @modify-qty="handleModifyQty"
      @toggle-vip="handleToggleVip"
      @clear-member="handleClearMember"
      @checkout="handleCheckout"
    />

    <!-- 支付弹窗 -->
    <MealPayModal
      :visible="showPayModal"
      :total-amount="totalPrice"
      @close="showPayModal = false"
      @pay="handlePay"
      @scan-pay="handleScanPay"
    />

    <!-- 规格选择弹窗 -->
    <MealSpecModal :visible="showSpecModal" :product="currentProduct" @close="showSpecModal = false"
      @add-to-cart="handleAddToCart" />

    <!-- 会员键盘弹窗 -->
    <MemberKeyboardModal
      :visible="showMemberKeyboard"
      @confirm="handleKeyboardConfirm"
      @close="showMemberKeyboard = false"
    />
  </div>
</template>

<script>
import MealCategorySidebar from '@/components/pos/MealCategorySidebar.vue'
import MealGoodsGrid from '@/components/pos/MealGoodsGrid.vue'
import MealCartPanel from '@/components/pos/MealCartPanel.vue'
import MealPayModal from '@/components/pos/MealPayModal.vue'
import MealSpecModal from '@/components/pos/MealSpecModal.vue'
import MemberKeyboardModal from '@/components/pos/MemberKeyboardModal.vue'
import { searchShopVipUser, computeGiveGold } from '@/api/goods/goodCashierApi'
import { queryProduct, queryProductCategory, queryProductSpecValue } from '@/api/goods/productApi'
import { pcUnifiedOrder, pcQrCodeUnifiedOrderPre, pcQrCodeUnifiedOrderCheck } from '@/api/goods/goodCashierConfirmApi'
import { getShopId } from '@/api/shop/shopApi'

export default {
  name: 'MealPos',
  components: {
    MealCategorySidebar,
    MealGoodsGrid,
    MealCartPanel,
    MealPayModal,
    MealSpecModal,
    MemberKeyboardModal
  },
  data() {
    return {
      loading: false,
      currentCategory: '',
      products: [],
      productCategorys: [],
      cart: [],
      isVip: false,
      member: null,
      showPayModal: false,
      showSpecModal: false,
      showMemberKeyboard: false,
      currentProduct: null,
      currentOrderId: null
    }
  },
  computed: {
    totalPrice() {
      let subTotal = this.cart.reduce((sum, item) => sum + (item.price * item.qty), 0)
      if (this.isVip) {
        subTotal = subTotal * 0.88 // VIP 88折
      }
      return subTotal
    }
  },
  mounted() {
    this.loadProductCategorys()
    this.loadProducts()
  },
  methods: {
    // 加载商品分组
    async loadProductCategorys() {
      try {
        const shopId = await getShopId()
        const res = await queryProductCategory({
          page: 1,
          row: 50,
          shopId: shopId
        })
        if (res.data) {
          this.productCategorys = res.data
        }
      } catch (error) {
        console.error('加载商品分组失败:', error)
      }
    },

    // 加载商品列表
    async loadProducts() {
      this.loading = true
      try {
        const shopId = await getShopId()
        const params = {
          shopId: shopId,
          page: 1,
          row: 1000
        }
        // 如果选择了分类，添加分类ID过滤
        if (this.currentCategory) {
          params.categoryId = this.currentCategory
        }
        const res = await queryProduct(params)

        if (res.data && res.data.length > 0) {
          // 转换商品数据格式，使用 defaultSpecValue 中的数据
          this.products = res.data.map(item => {
            const defaultSpec = item.defaultSpecValue || {}
            return {
              id: item.productId,
              name: item.prodName,
              price: parseFloat(defaultSpec.price || defaultSpec.ownerPrice || 0),
              vipPrice: parseFloat(defaultSpec.vipPrice || defaultSpec.ownerPrice || 0),
              coverPhoto: item.coverPhoto || '',
              specs: [], // 规格信息需要单独查询
              valueId: defaultSpec.valueId || item.valueId,
              productId: item.productId,
              specName: defaultSpec.specName || item.specName || '',
              categoryId: item.categoryId || '',
              categoryName: item.categoryName || '',
              stock: parseFloat(defaultSpec.stock || item.stock || 0)
            }
          })
        } else {
          this.products = []
        }
      } catch (error) {
        console.error('加载商品失败:', error)
        this.$message.error('加载商品失败')
      } finally {
        this.loading = false
      }
    },

    // 分类切换
    handleCategoryChange(categoryId) {
      this.currentCategory = categoryId
      this.loadProducts()
    },

    // 搜索
    handleSearch(value) {
      // 搜索逻辑在 MealGoodsGrid 组件内部处理
    },

    // 添加到购物车
    handleAddToCart(product) {
      const spec = product.spec || ''
      const uid = product.id + spec
      const exist = this.cart.find(x => x.uid === uid)

      if (exist) {
        exist.qty++
      } else {
        this.cart.push({
          uid,
          id: product.id,
          name: product.name,
          price: this.isVip && product.vipPrice ? product.vipPrice : product.price,
          vipPrice: product.vipPrice || product.price,
          spec,
          qty: 1,
          valueId: product.valueId || product.id,
          productId: product.productId || product.id,
          coverPhoto: product.coverPhoto || '',
          specName: product.specName || spec
        })
      }
    },

    // 处理商品点击
    async handleProductClick(product) {
      try {
        const shopId = await getShopId()
        // 查询商品规格值列表
        const res = await queryProductSpecValue({
          productId: product.id,
          shopId: shopId,
          page: 1,
          row: 100
        })

        if (res.data && res.data.length > 0) {
          // 如果有多个规格，打开选择弹窗
          if (res.data.length > 1) {
            this.currentProduct = {
              ...product,
              specValues: res.data // 规格值列表
            }
            this.showSpecModal = true
          } else {
            // 如果只有一个规格，直接添加到购物车
            const specValue = res.data[0]
            this.handleAddToCart({
              ...product,
              valueId: specValue.valueId,
              price: parseFloat(specValue.price || specValue.ownerPrice || 0),
              vipPrice: parseFloat(specValue.vipPrice || specValue.ownerPrice || 0),
              specName: specValue.specName || '',
              spec: specValue.specName || ''
            })
          }
        } else {
          // 没有规格值，直接添加
          this.handleAddToCart(product)
        }
      } catch (error) {
        console.error('查询商品规格失败:', error)
        this.$message.error('查询商品规格失败')
      }
    },

    // 打开规格选择
    handleOpenSpec(product) {
      this.currentProduct = product
      this.showSpecModal = true
    },

    // 修改数量
    handleModifyQty(uid, delta) {
      const item = this.cart.find(x => x.uid === uid)
      if (item) {
        item.qty += delta
        if (item.qty <= 0) {
          this.cart = this.cart.filter(x => x.uid !== uid)
        }
      }
    },

    // 切换VIP - 点击后打开会员查询键盘
    handleToggleVip() {
      this.showMemberKeyboard = true
    },

    // 清除会员
    handleClearMember() {
      this.isVip = false
      this.member = null
      // 恢复原价
      this.cart.forEach(item => {
        item.price = item.vipPrice || item.price
      })
    },

    // 会员键盘确认
    handleKeyboardConfirm(value) {
      if (!value || !value.trim()) {
        this.$message.warning('请输入会员手机号')
        return
      }
      this.queryMember(value)
    },

    // 查询会员
    async queryMember(phone) {
      try {
        const res = await searchShopVipUser({
          page: 1,
          row: 1,
          vipUserSearchValue: phone.trim()
        })

        const vip = Array.isArray(res.data) ? (res.data.length > 0 ? res.data[0] : null) : (res.data && res.data.vipId ? res.data : null)

        if (vip && vip.vipId) {
          this.member = {
            vipId: vip.vipId || '',
            name: vip.vipName || '',
            tel: vip.vipTel || '',
            level: vip.levelName || '',
            sex: vip.sex || '',
            balance: parseFloat(vip.balance || 0),
            integralAmount: parseFloat(vip.integralAmount || 0),
            couponCount: parseInt(vip.couponCount || 0),
            discount: '享9折优惠',
            discountRate: 90
          }

          // 开启VIP状态
          this.isVip = true

          // 更新购物车价格为会员价
          this.cart.forEach(item => {
            if (item.vipPrice) {
              item.price = item.vipPrice
            }
          })

          // 计算赠送积分
          await this.computeVipUserGold()

          this.$message.success('会员查询成功')
          this.showMemberKeyboard = false
        } else {
          this.$message.warning('未找到会员信息')
        }
      } catch (error) {
        console.error('查询会员失败:', error)
        this.$message.error('查询会员失败')
      }
    },

    // 计算赠送积分
    async computeVipUserGold() {
      if (!this.member || !this.member.vipId) {
        return
      }

      try {
        const amount = this.totalPrice
        const res = await computeGiveGold({
          amount: amount,
          vipId: this.member.vipId
        })

        if (res.data) {
          if (res.data.integralAmount !== undefined) {
            this.member.integralAmount = parseFloat(res.data.integralAmount || 0)
          }
          if (res.data.goldCount !== undefined) {
            this.member.goldCount = parseFloat(res.data.goldCount || 0)
          }
        }
      } catch (error) {
        console.error('计算赠送积分失败:', error)
      }
    },

    // 结算
    handleCheckout() {
      if (this.cart.length === 0) {
        this.$message.warning('购物车为空')
        return
      }
      this.showPayModal = true
    },

    // 支付
    async handlePay(payData) {
      try {
        if (payData.method === 'scan') {
          this.$message.warning('扫码枪支付请在扫码模式下扫描顾客付款码')
          return
        }

        const shopId = await getShopId()

        // 构建商品列表
        const storeOrderCarts = this.cart.map(item => ({
          cateType: '1001',
          productId: item.productId || item.id,
          prodName: item.name,
          valueId: item.valueId,
          goodsNum: item.qty,
          storeId: '',
          shopId: shopId,
          discountType: '1001',
          discountRate: '1',
          discountPrice: 0,
          cartType: '3306'
        }))

        // 构建订单信息
        const storeOrder = {
          personId: '-1',
          personName: (this.member && this.member.name) || '未知',
          payPrice: payData.amount.toString(),
          discountType: '1001',
          discountRate: '1',
          discountPrice: '0',
          payMethod: payData.method
        }

        // 构建会员信息
        const vipUsers = this.member ? {
          vipId: this.member.vipId || '',
          vipName: this.member.name || '',
          vipTel: this.member.tel || '',
          levelName: this.member.level || '',
          sex: this.member.sex || '',
          integralAmount: this.member.integralAmount || 0,
          couponCount: this.member.couponCount || 0
        } : {}

        // 调用 PC 统一下单接口
        const unifiedOrderParam = {
          storeOrder: storeOrder,
          storeOrderCarts: storeOrderCarts,
          shopId: shopId
        }
        if (this.member && this.member.vipId) {
          unifiedOrderParam.vipUsers = vipUsers
        }

        const unifiedOrderRes = await pcUnifiedOrder(unifiedOrderParam)

        if (unifiedOrderRes.code !== 0) {
          this.$message.error(unifiedOrderRes.msg || '下单失败')
          return
        }

        const orderId = (unifiedOrderRes.data && unifiedOrderRes.data.orderId) || unifiedOrderRes.orderId || null
        this.currentOrderId = orderId

        this.$message.success(unifiedOrderRes.msg || '下单成功')
        this.cart = []
        this.member = null
        this.isVip = false
        this.showPayModal = false

        // 如果有订单号，则打开小票打印
        if (orderId) {
          this.openOrderPrint({ orderId })
        }
      } catch (error) {
        console.error('下单失败:', error)
        this.$message.error(error.msg || '下单失败')
      }
    },

    // 打开订单打印窗口
    openOrderPrint(orderData) {
      if (orderData && orderData.orderId) {
        window.open(`/smallPrint.html#/pages/goods/printSmallOrder?orderId=${orderData.orderId}&autoPrint=Y`)
      }
    },

    // 扫码支付（扫码枪 / 被扫）
    handleScanPay(scanCode) {
      this._handleScanGunPay(scanCode)
    },

    // 扫码枪预下单 + 轮询确认
    async _handleScanGunPay(scanCode) {
      try {
        const shopId = await getShopId()

        // 构建商品列表
        const storeOrderCarts = this.cart.map(item => ({
          cateType: '1001',
          productId: item.productId || item.id,
          prodName: item.name,
          valueId: item.valueId || item.id,
          goodsNum: item.qty,
          storeId: '',
          shopId: shopId,
          discountType: '1001',
          discountRate: '1',
          discountPrice: 0,
          cartType: '3306'
        }))

        // 构建订单信息（扫码支付方式）
        const storeOrder = {
          personId: '-1',
          personName: (this.member && this.member.name) || '未知',
          payPrice: this.totalPrice.toString(),
          discountType: '1001',
          discountRate: '1',
          discountPrice: '0',
          payMethod: 'scangun'
        }

        // 构建会员信息
        const vipUsers = this.member ? {
          vipId: this.member.vipId || '',
          vipName: this.member.name || '',
          vipTel: this.member.tel || '',
          levelName: this.member.level || '',
          sex: this.member.sex || '',
          integralAmount: this.member.integralAmount || 0,
          couponCount: this.member.couponCount || 0
        } : {}

        // 调用扫码枪预下单接口
        const preParam = {
          storeOrder: storeOrder,
          storeOrderCarts: storeOrderCarts,
          shopId: shopId,
          scanCode: scanCode
        }
        if (this.member && this.member.vipId) {
          preParam.vipUsers = vipUsers
        }

        const preRes = await pcQrCodeUnifiedOrderPre(preParam)
        if (preRes.code !== 0) {
          this.$message.error(preRes.msg || '预下单失败')
          return
        }

        // 保存订单上下文（用于轮询和打印）
        this.currentOrderId =
          (preRes.data && preRes.data.orderId) || preRes.orderId || null

        // 轮询检查下单结果
        this._checkQrOrderResult({
          shopId: shopId,
          scanCode: scanCode,
          orderId: this.currentOrderId
        })
      } catch (error) {
        console.error('扫码支付预下单失败:', error)
        this.$message.error(error.msg || '扫码支付预下单失败')
      }
    },

    // 轮询查询扫码订单结果
    async _checkQrOrderResult(param) {
      try {
        const res = await pcQrCodeUnifiedOrderCheck(param)

        if (res.code === 0) {
          this.$message.success(res.msg || '支付成功')
          this.cart = []
          this.member = null
          this.isVip = false
          this.showPayModal = false

          const orderId =
            (res.data && res.data.orderId) || param.orderId || this.currentOrderId
          if (orderId) {
            this.openOrderPrint({ orderId })
          }
        } else if (res.code === 41) {
          // 支付处理中，继续轮询
          setTimeout(() => {
            this._checkQrOrderResult(param)
          }, 3000)
        } else {
          this.$message.error(res.msg || '扫码支付失败')
        }
      } catch (error) {
        console.error('扫码支付查询失败:', error)
        this.$message.error(error.msg || '扫码支付查询失败')
      }
    }
  }
}
</script>

<style scoped>
.pos-container {
  background: #F7F9FC;
  height: 100vh;
  display: flex;
  overflow: hidden;
  font-family: -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif;
}

/* 引入 Font Awesome 图标 */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');
</style>
